import * as React from "react";
import { Icon, IconInput } from "../components/Icon";
import { Button } from "../components/Button";
import { CurriculumTemplateSummary } from "./CurriculumTemplatesStore";

/** Short catalogue copy; full setup notes remain available inside the template. */
const TEMPLATE_PRESENTATION: Record<string, { icon: IconInput; title?: string; summary: string; facts: string[] }> = {
    CAPS: {
        icon: ["fad", "graduation-cap"],
        summary: "A starting point for South African secondary school assessment.",
        facts: ["Grades 10–12", "4 terms"],
    },
    Cambridge: {
        icon: ["fad", "book"],
        summary: "International school qualifications, including IGCSE.",
        facts: ["International"],
    },
    IEB: {
        icon: ["fad", "award"],
        summary: "School-based assessment and examinations for the NSC.",
        facts: ["Grades 10–12", "4 terms"],
    },
    Tertiary: {
        icon: ["fad", "building-columns"],
        summary: "A foundation for higher education programmes.",
        facts: ["Higher education"],
    },
    BloomGrade5MathsProof: {
        icon: ["fad", "chart-simple"],
        title: "Grade 5 Mathematics",
        summary: "A reporting demonstration, not an official CAPS curriculum.",
        facts: ["Grade 5", "Reporting demo"],
    },
};

/** The wizard's step keys, as a card names them. */
const STEP_NAMES: Record<string, string> = {
    structure: "structure",
    subjects: "subjects",
    terms: "terms",
    weightings: "weightings & rules",
    assessments: "assessments",
    review: "ready to apply",
};

export interface CurriculumTemplateCardProps {
    template: CurriculumTemplateSummary;
    onOpen: (key: string) => void;
}

export const CurriculumTemplateCard: React.FC<CurriculumTemplateCardProps> = ({ template, onOpen }) => {
    const lastApplication = template.applications[0];
    const presentation = TEMPLATE_PRESENTATION[template.key];
    const title = presentation?.title ?? template.displayName;
    const status = !template.hasSeedData ? "Not configured" : template.draft ? "In progress" : lastApplication ? "Applied" : "Available";
    const statusClass = !template.hasSeedData
        ? "bg-surface-muted text-text-weak"
        : template.draft
          ? "bg-primary-soft text-primary-text"
          : lastApplication
            ? "bg-success-soft text-success-text"
            : "bg-surface-muted text-text";

    return (
        <article className="flex min-w-0 flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card transition-shadow hover:shadow-card-hover">
            <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center justify-between gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-soft text-xl text-primary-text">
                        <Icon icon={presentation?.icon ?? ["fad", "book"]} />
                    </span>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${statusClass}`}>{status}</span>
                </div>

                <h3 className="mt-4 text-lg font-semibold tracking-tight text-text-strong">{title}</h3>
                <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-text">
                    {presentation?.summary ?? template.description}
                </p>

                {presentation?.facts.length > 0 && (
                    <ul aria-label="Template highlights" className="mt-4 flex flex-wrap gap-2">
                        {presentation.facts.map((fact) => (
                            <li key={fact} className="rounded-sm bg-surface-muted px-2 py-1 text-[11px] font-medium text-text">{fact}</li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="space-y-3 border-t border-border px-5 py-4">
                {(lastApplication || template.draft) && (
                    <div className="space-y-1 text-[11px] text-text-weak">
                        {template.draft && (
                            <p>
                                {template.draft.currentStep ? `Continue with ${STEP_NAMES[template.draft.currentStep] ?? template.draft.currentStep}` : "Setup started"}
                                {" · Saved "}{new Date(template.draft.updatedAt).toLocaleDateString()}
                            </p>
                        )}
                        {lastApplication && (
                            <p>
                                Last applied {new Date(lastApplication.appliedAt).toLocaleDateString()}
                                {template.applications.length > 1 ? ` · ${template.applications.length} applications` : ""}
                            </p>
                        )}
                    </div>
                )}
                <Button
                    variant={template.hasSeedData && template.draft ? "primary" : "secondary"}
                    size="md"
                    className="min-h-[40px]"
                    fullWidth
                    aria-label={`${!template.hasSeedData ? "View details for" : template.draft ? "Continue setup for" : "Set up"} ${template.displayName}`}
                    onClick={() => onOpen(template.key)}
                >
                    <span className="flex-1 text-left">{!template.hasSeedData ? "View details" : template.draft ? "Continue setup" : "Set up template"}</span>
                    <Icon icon={["fad", "chevron-right"]} />
                </Button>
            </div>
        </article>
    );
};
